import React, { useState, useMemo, useEffect } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';
import Fab from '@material-ui/core/Fab';
import { FormControlLabel, FormHelperText, InputAdornment, Switch } from '@material-ui/core';
import ProjectDialog from '../../../customComponentV2/dialog/ProjectDialog'; 
import AppFunctionsService from '../../../../services/appFunctionsService';
import { ITransformer, ITransformerCompany } from '../../../../@types/model/transformer/transformer';
import { IRootState } from '../../../../@types/redux';
import { connect } from 'react-redux';
import TransformerFunctions from '../../../../store/transformer/functions';
import SPUBudgetForm from '../form/SPUForm';
import { Moment } from 'moment';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { IUserSession } from '../../../../@types/employee';
import moment from 'moment';
import GeneralFunctions from '../../../../store/general/functions';
import MunicipalityBudgetForm from '../form/MunicipalityForm';
import LPUBudgetForm from '../form/LPUForm';
import CompanySelector from '../../../customComponents/selector/CompanySelector';
import { ICompany } from '../../../../@types/model/masterData/company';
import TransformerAreaDropdown from '../../../customComponents/selector/transformer/AreaSelector';
import TransformerDropdown from '../../../customComponents/selector/transformer/TransformerSelector';
import TransformerAccountDropdown from '../../../customComponents/selector/transformer/TransformerAccountSelector';
import PeriodDropdown from '../../../customComponents/selector/PeriodSelector';
import OutlinedTextField from '../../../customComponents/textField/OutlinedTextField';
import FormCheckbox from '../../../customComponentV2/input/FormCheckbox';
import LabelledCheckbox from '../../../customComponentV2/input/LabelledCheckbox';
import { CustomChangeEvent } from '../../../../@types/helper';
import TransformerBudgetHelper, { BudgetType, ITransformerBudget, ISPUBudgetFormValues, ISPUBudget, ILPUBudgetFormValues, ILPUBudget, IMunicipalityBudgetFormValues, IMunicipalityBudget } from '../../../../@types/model/transformer/budget/budget';

interface IBudgetEditDialogProps {
    type : BudgetType;
    value ?: ITransformerBudget;
    isLoadingTransformers : boolean;
    
    transformers : Array<ITransformer>;
    session ?: IUserSession | null;
    budgets : Array<ITransformerBudget>;
    companies : Array<ICompany>;
}

const BudgetEditDialog = (props : IBudgetEditDialogProps) : React.ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [hideDetail, setHideDetail] = useState<boolean>(false);
    const [date, setDate] = useState<Moment | null>(null);
    const [company, setCompany] = useState<ITransformerCompany | null>(null);
    const [area, setArea] = useState<string | null>(null);
    const [selectedTransformerEPNumber, setSelectedTransformerEPNumber] = useState<string | null>(null);
    const [accountNumber, setAccountNumber] = useState<string | null>(null);
    const [period, setPeriod] = useState<number | null>(null);

    useEffect(() => {
        TransformerFunctions.getList();
    }, [isOpen]);

    const onEdit = (): void => {
        setIsOpen(true);
        setDate(props.value?.startDate ? moment.utc(props.value.startDate) : null);
        setArea(props.value?.area ?? null);
        setCompany(props.companies.find(x => x.id === props.value?.companyId) ?? null);
    };

    const onClose = (): void => {
        setIsOpen(false);
        setIsSaving(false);
    };

    const tooltip = useMemo<string>(() => {
        if (!props.value) return 'Add';
        return 'Edit';
    }, [props.value]);

    const title = useMemo<string>(() => {
        let titleText = props.type.toLocaleUpperCase();

        if (props.type === 'municipality') {
            titleText = AppFunctionsService.toTitleCase(props.type);
        }

        return `${tooltip === 'Add' ? 'Create' : 'View'} ${titleText} Budget`;
    }, [props.type, tooltip]);
    
    const isLoading = useMemo<boolean>(() => {
        return props.isLoadingTransformers || isSaving;
    }, [props.isLoadingTransformers, isSaving]);

    const onHideDetailChange = (event: CustomChangeEvent, checked: boolean): void => {
        setHideDetail(checked);
    };

    const onDateChange = (newDate: MaterialUiPickersDate): void => {
        setDate(newDate?.asUTC().startOf('month') ?? null);
    };

    const onCompanyChange = (selectedCompany?: ICompany | null): void => {
        setCompany(!selectedCompany ? null : {
            code: selectedCompany.code,
            electricityAccountNumber: selectedCompany.electricityAccountNumber,
            id: selectedCompany.id,
            interestAccountNumber: selectedCompany.interestAccountNumber,
            name: selectedCompany.name,
            vatAccountNumber: selectedCompany.vatAccountNumber,
            paymentEnabled: selectedCompany.paymentEnabled,
            guid: selectedCompany.guid,
            isActive: selectedCompany.isActive,
        });
    };

    const onSPUSubmit = async (values: ISPUBudgetFormValues): Promise<void> => {
        if (!props.session) return;
        const isDuplicate = props.budgets.some(x =>
            x.id !== values.id &&
            x.area === values.area &&
            x.companyId === values.companyId &&
            x.startDate === values.startDate
        );
        if (isDuplicate) {
            GeneralFunctions.generalShowErrorSnackbar('Trying to save duplicate');
            return;
        }
        const save : ITransformerBudget<ISPUBudget> = {
            id: values.id,
            isActive: props.value?.isActive ?? true,
            createdBy: props.value?.createdBy ?? props.session.firebaseUser.uid,
            createdByName: props.value?.createdByName ?? props.session.firebaseUser.displayName ?? '',
            createdByEmployee: props.value?.createdByEmployee ?? props.session.employee.EmployeeNumber ?? '',
            createdOn: props.value?.createdOn ?? moment.utc().valueOf(),
            updatedBy: props.session.firebaseUser.uid,
            updatedByName: props.session.firebaseUser.displayName ?? '',
            updatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            updatedOn: moment.utc().valueOf(),
            isWeb: true,
            endDate: values.endDate,
            startDate: values.startDate,
            type: 'spu',
            year: values.year,
            area: values.area,
            companyId: values.companyId,
            companyName: values.companyName,
            values: values.values.map(x => {
                const { index: _index, ...rest } = x;
                return { ...rest, budget: !x.budget ? null : Number(x.budget) };
            }),
            prevBudget: null,
        };
        try {
            setIsSaving(true);
            await TransformerBudgetHelper.save(save);
            GeneralFunctions.generalShowSuccessSnackbar('Saved');
            onClose();
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, 'Client saving Error');
        } finally {
            setIsSaving(false);
        }
    };

    const onLPUSubmit = async (values: ILPUBudgetFormValues): Promise<void> => {
        if (!props.session) return;
        const isDuplicate = props.budgets.some(x =>
            x.id !== values.id &&
            x.area === values.area &&
            x.companyId === values.companyId &&
            x.startDate === values.startDate
        );
        if (isDuplicate) {
            GeneralFunctions.generalShowErrorSnackbar('Trying to save duplicate');
            return;
        }
        const save : ITransformerBudget<ILPUBudget> = {
            id: values.id,
            isActive: props.value?.isActive ?? true,
            createdBy: props.value?.createdBy ?? props.session.firebaseUser.uid,
            createdByName: props.value?.createdByName ?? props.session.firebaseUser.displayName ?? '',
            createdByEmployee: props.value?.createdByEmployee ?? props.session.employee.EmployeeNumber ?? '',
            createdOn: props.value?.createdOn ?? moment.utc().valueOf(),
            updatedBy: props.session.firebaseUser.uid,
            updatedByName: props.session.firebaseUser.displayName ?? '',
            updatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            updatedOn: moment.utc().valueOf(),
            isWeb: true,
            endDate: values.endDate,
            startDate: values.startDate,
            type: 'lpu',
            year: values.year,
            area: values.area,
            companyId: values.companyId,
            companyName: values.companyName,
            values: values.values.map(x => {
                const { index: _index, ...rest } = x;
                return ({ ...rest, offpeakBudget: !x.offpeakBudget ? null : Number(x.offpeakBudget), peakBudget: !x.peakBudget ? null : Number(x.peakBudget), standardBudget: !x.standardBudget ? null : Number(x.standardBudget), totalBudget: (x.standardBudget ?? 0) + (x.peakBudget ?? 0) + (x.offpeakBudget ?? 0) });
            }),
            prevBudget: null,
        };
        try {
            setIsSaving(true);
            await TransformerBudgetHelper.save(save);
            GeneralFunctions.generalShowSuccessSnackbar('Saved');
            onClose();
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, 'Client saving Error');
        } finally {
            setIsSaving(false);
        }
    };

    const onMunicipalitySubmit = async (values: IMunicipalityBudgetFormValues): Promise<void> => {
        if (!props.session) return;
        const isDuplicate = props.budgets.some(x =>
            x.id !== values.id &&
            x.area === values.area &&
            x.companyId === values.companyId &&
            x.startDate === values.startDate
        );
        if (isDuplicate) {
            GeneralFunctions.generalShowErrorSnackbar('Trying to save duplicate');
            return;
        }
        const save : ITransformerBudget<IMunicipalityBudget> = {
            id: values.id,
            isActive: props.value?.isActive ?? true,
            createdBy: props.value?.createdBy ?? props.session.firebaseUser.uid,
            createdByName: props.value?.createdByName ?? props.session.firebaseUser.displayName ?? '',
            createdByEmployee: props.value?.createdByEmployee ?? props.session.employee.EmployeeNumber ?? '',
            createdOn: props.value?.createdOn ?? moment.utc().valueOf(),
            updatedBy: props.session.firebaseUser.uid,
            updatedByName: props.session.firebaseUser.displayName ?? '',
            updatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            updatedOn: moment.utc().valueOf(),
            isWeb: true,
            endDate: values.endDate,
            startDate: values.startDate,
            type: 'municipality',
            year: values.year,
            area: values.area,
            companyId: values.companyId,
            companyName: values.companyName,
            values: values.values.map(x => {
                const { index: _index, ...rest } = x;
                return { ...rest, budget: !x.budget ? null : Number(x.budget) };
            }),
            prevBudget: null,
        };
        try {
            setIsSaving(true);
            await TransformerBudgetHelper.save(save);
            GeneralFunctions.generalShowSuccessSnackbar('Saved');
            onClose();
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, 'Client saving Error');
        } finally {
            setIsSaving(false);
        }
    };

    const onAreaChange = (newArea?: string | null): void => {
        setArea(newArea ?? null);
    };

    const onTransformerChange = (selectedValue?: ITransformer): void => {
        setSelectedTransformerEPNumber(selectedValue?.EPNumber ?? null);
    };

    const onAccountNumberChange = (selectedValue?: ITransformer | null): void => {
        setAccountNumber(selectedValue?.AccountNumber ?? null);
    };

    const onPeriodChange = (newValue: number | null): void => {
        setPeriod(newValue);
    };

    return (
        <React.Fragment>
            <Tooltip title={tooltip}>
                <div>
                    {
                        tooltip === 'Add' &&
                        <Fab size='small' color='secondary' className='bcy' aria-label='Add' onClick={onEdit} >
                            <Icon>add</Icon>
                        </Fab>
                    }
                    {
                        tooltip !== 'Add' &&
                        <Fab size='small' color='secondary' className='bcy' variant='extended' aria-label='View' onClick={onEdit} >
                            <span className='pl10 pr10'>{tooltip}</span>
                        </Fab>
                    }
                </div>
            </Tooltip>
            <ProjectDialog
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                fullScreen
                isLoadingCircular={isLoading}
            >
                <div className='fdc flx1 hfill bcg0 p10 pt20'>
                    <div className='fdr aifs jcfs mb15'>
                        <div className='fdc flx1 aifs jcfs mr15 mb8'>
                            <DatePicker
                                label={'Start Date'}
                                onChange={onDateChange}
                                value={date}
                                disabled={!!props.value}
                                format={'MMM YYYY'}
                                views={['month', 'year']}
                                fullWidth
                                TextFieldComponent={OutlinedTextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <Icon>date_range</Icon>
                                        </InputAdornment>
                                    ),
                                }}
                                error={!date}
                                required
                            />
                            { !date && <FormHelperText error>Required</FormHelperText> }
                        </div>
                        <div className='fdc flx1 aifs jcfs mr15 mb8'>
                            <CompanySelector
                                value={company?.id}
                                onChange={onCompanyChange}
                                disabled={!!props.value || isLoading}
                                required
                            />
                            { !company && <FormHelperText error>Required</FormHelperText> }
                        </div>
                        <div className='fdr flx1 aifs jcfs mr15'>
                            <TransformerAreaDropdown
                                label='Area'
                                type={props.type}
                                fullWidth
                                value={area}
                                onChange={onAreaChange}
                                disabled={!!props.value || isLoading}
                                required
                            />
                        </div>
                        <div className='fdr flx1 aifs jcfs mr15'>
                            <TransformerDropdown
                                value={selectedTransformerEPNumber ?? ''}
                                fullWidth
                                onChange={onTransformerChange}
                                type={props.type}
                            />
                        </div>
                        <div className='fdr flx1 aifs jcfs mr15'>
                            <TransformerAccountDropdown
                                value={accountNumber ?? ''}
                                fullWidth
                                onChange={onAccountNumberChange}
                                type={props.type}
                            />
                        </div>
                        <div className='fdr flx1 aifs jcfs mr15 mb8'>
                            <PeriodDropdown
                                value={period}
                                fullWidth
                                onChange={onPeriodChange}
                                startMonth={date?.valueOf() ?? 0}
                                disabled={!date}
                            />
                        </div>
                        <div className='fdr aic jcc mt12'>
                            <LabelledCheckbox
                                name='hideDetail'
                                label={'Hide Detail'}
                                onChange={onHideDetailChange}
                                value={hideDetail}
                            />
                        </div>
                    </div>
                    {
                        props.type === 'spu' && !!date && !!area && !!company &&
                        <div className='fdc flx1'>
                            <SPUBudgetForm
                                hideDetail={hideDetail}
                                startDate={date.valueOf()}
                                transformers={props.transformers}
                                initialValue={props.value}
                                onSubmit={onSPUSubmit}
                                accountNumber={accountNumber}
                                area={area}
                                company={company}
                                period={period}
                                transformer={selectedTransformerEPNumber}
                            />
                        </div>
                    }
                    {
                        props.type === 'lpu' && !!date && !!area && !!company &&
                        <div className='fdc flx1'>
                            <LPUBudgetForm
                                hideDetail={hideDetail}
                                startDate={date.valueOf()}
                                transformers={props.transformers}
                                initialValue={props.value}
                                onSubmit={onLPUSubmit}
                                accountNumber={accountNumber}
                                area={area}
                                company={company}
                                period={period}
                                transformer={selectedTransformerEPNumber}
                            />
                        </div>
                    }
                    {
                        props.type === 'municipality' && !!date && !!area && !!company &&
                        <div className='fdc flx1'>
                            <MunicipalityBudgetForm
                                hideDetail={hideDetail}
                                startDate={date.valueOf()}
                                transformers={props.transformers}
                                initialValue={props.value}
                                onSubmit={onMunicipalitySubmit}
                                accountNumber={accountNumber}
                                area={area}
                                company={company}
                                period={period}
                                transformer={selectedTransformerEPNumber}
                            />
                        </div>
                    }
                </div>
            </ProjectDialog>
        </React.Fragment>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoadingTransformers: state.transformer.loading,
        session: state.auth.session,
        companies: state.data.companies,
    };
};

export default connect(
    mapStateToProps,
)(BudgetEditDialog);
